/**
 * Component for image lightbox functionality
 */
export class LightboxComponent {
  private currentLightbox: HTMLElement | null = null;
  private escapeHandler: ((e: KeyboardEvent) => void) | null = null;

  /**
   * Open lightbox with an image
   * @param src - Image source URL
   * @param alt - Image alt text
   */
  open(src: string, alt: string): void {
    // Close existing lightbox if any
    this.close();

    // Ensure the image path is properly resolved
    const imgSrc = src.startsWith('http') ? src : `./${src}`;

    // Create lightbox element
    const lightbox = document.createElement('div');
    lightbox.className = 'lightbox fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4';
    lightbox.innerHTML = `
      <div class="relative max-w-sm max-h-full">
        <button
          class="lightbox-close absolute -top-12 right-0 text-white hover:text-gray-300 text-xl font-bold focus:outline-none focus:ring-2 focus:ring-white rounded p-2"
          aria-label="Close lightbox">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        <img src="${imgSrc}" alt="${alt}" class="max-w-full max-h-full object-contain rounded-lg shadow-2xl" style="max-width: 375px;" onerror="this.style.display='none'; this.nextElementSibling.style.display='block';">
        <div class="hidden text-white text-center p-4">Image not available</div>
        <div class="absolute -bottom-8 left-0 text-white text-sm">${alt}</div>
      </div>
    `;

    // Add event listeners
    this.setupEventListeners(lightbox);

    // Add to DOM
    document.body.appendChild(lightbox);
    document.body.style.overflow = 'hidden';

    // Store reference
    this.currentLightbox = lightbox;

    // Focus close button for accessibility
    setTimeout(() => {
      const closeBtn = lightbox.querySelector('.lightbox-close') as HTMLButtonElement;
      if (closeBtn) closeBtn.focus();
    }, 100);
  }

  /**
   * Close the lightbox
   */
  close(): void {
    if (!this.currentLightbox) return;

    // Remove escape handler
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
      this.escapeHandler = null;
    }

    // Remove lightbox
    document.body.removeChild(this.currentLightbox);
    document.body.style.overflow = '';
    this.currentLightbox = null;
  }

  /**
   * Setup event listeners for lightbox
   */
  private setupEventListeners(lightbox: HTMLElement): void {
    // Close on backdrop click
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) {
        this.close();
      }
    });

    // Close on close button click
    const closeBtn = lightbox.querySelector('.lightbox-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Close on Escape key
    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };
    document.addEventListener('keydown', this.escapeHandler);
  }

  /**
   * Check if lightbox is currently open
   */
  isOpen(): boolean {
    return this.currentLightbox !== null;
  }
}
